import React, { useCallback, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';

import { useUpdateBundle } from '~/hooks/apis/accommodation/useBundles';

import { Bundle, UpdateBundlePayload } from '~/services/AccommodationService';

interface Props {
  bundle: Bundle;
}

type ScheduleRow = NonNullable<UpdateBundlePayload['schedules']>[number];

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

function AccommodationBundleSchedulesTab({ bundle }: Props) {
  const initialSchedules: Array<ScheduleRow> = (bundle.schedules ?? []).map((s) => ({
    brands: s.brands ?? [],
    activeStartDate: s.activeStartDate ?? '',
    activeEndDate: s.activeEndDate ?? '',
    travelStartDate: s.travelStartDate ?? '',
    travelEndDate: s.travelEndDate ?? '',
  }));

  const [schedules, setSchedules] = useState<Array<ScheduleRow>>(initialSchedules);
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => initialSchedules.map(allocateRowKey));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateBundleMutation = useUpdateBundle();

  const updateRow = useCallback((index: number, key: keyof ScheduleRow, value: string | Array<string>) => {
    setSchedules((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  }, []);

  const addRow = useCallback(() => {
    setSchedules((prev) => [
      ...prev,
      {
        brands: [],
        activeStartDate: '',
        activeEndDate: '',
        travelStartDate: '',
        travelEndDate: '',
      },
    ]);
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, []);

  const removeRow = useCallback((index: number) => {
    setSchedules((prev) => prev.filter((_, i) => i !== index));
    setRowKeys((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      try {
        await updateBundleMutation.mutateAsync({
          bundleId: bundle.id,
          payload: { schedules },
        });
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save schedules');
      }
    },
    [schedules, updateBundleMutation, bundle.id],
  );

  const isSubmitting = updateBundleMutation.isPending;

  return (
    <Box component="form" onSubmit={handleSave}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Schedules restrict when the bundle is sellable and which travel dates it covers. An empty list means the
          bundle is always active.
        </Typography>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {schedules.map((row, index) => (
          <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Schedule #{index + 1}</Typography>
              <IconButton onClick={() => removeRow(index)} size="small" aria-label="Remove schedule">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack spacing={2}>
              <TextField
                label="Brands (comma-separated)"
                value={row.brands.join(', ')}
                onChange={(e) =>
                  updateRow(
                    index,
                    'brands',
                    e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
                fullWidth
              />
              <Box display="flex" gap={2}>
                <TextField
                  label="Active Start Date"
                  type="date"
                  value={row.activeStartDate}
                  onChange={(e) => updateRow(index, 'activeStartDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Active End Date"
                  type="date"
                  value={row.activeEndDate}
                  onChange={(e) => updateRow(index, 'activeEndDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
              <Box display="flex" gap={2}>
                <TextField
                  label="Travel Start Date"
                  type="date"
                  value={row.travelStartDate}
                  onChange={(e) => updateRow(index, 'travelStartDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Travel End Date"
                  type="date"
                  value={row.travelEndDate}
                  onChange={(e) => updateRow(index, 'travelEndDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            </Stack>
          </Box>
        ))}

        <Box>
          <Button onClick={addRow} variant="outlined">
            + Add Schedule
          </Button>
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save Schedules'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default AccommodationBundleSchedulesTab;
